import React from 'react';
import { useTranslation } from 'react-i18next';
import { useScroll, useTransform } from 'framer-motion';
import { motion } from 'framer-motion';
import SuccessStories from './SuccessStories';
import { useBackendTranslation } from '../utils/useBackendTranslation';
import BackendText from './BackendText';
import Resources from './Resources';

const useArr = (t, key) => {
  const v = t(key, { returnObjects: true, defaultValue: [] });
  if (Array.isArray(v)) return v;
  if (v && typeof v === 'object') return Object.values(v);
  return [];
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.3
    }
  }
};

const scaleIn = {
  hidden: { scale: 0.85, opacity: 0 },
  visible: { 
    scale: 1, 
    opacity: 1,
    transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] }
  }
};

function Impact() {
  const { t } = useTranslation();
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0.3]);
  
  // Fetch translation data from i18n
  const outcomes = useArr(t, 'impact_page.outcomes');
  const milestones = useArr(t, 'impact_page.milestones');
  const reports = useArr(t, 'impact_page.reports.items');

  // Use our custom hook to translate backend data (works with translated data from i18n)
  const { translatedData: translatedOutcomes } = useBackendTranslation(outcomes);
  const { translatedData: translatedMilestones } = useBackendTranslation(milestones);

  return (
    <div className="min-h-screen bg-white text-black overflow-hidden">
      {/* Enhanced Background with Depth */}
      <div className="fixed inset-0 pointer-events-none">
        <motion.div style={{ opacity }} className="absolute inset-0">
          {/* Gradient mesh background */}
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-yellow-50 rounded-full filter blur-3xl opacity-30" />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-gray-50 rounded-full filter blur-3xl opacity-40" />
          <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-yellow-50 rounded-full filter blur-3xl opacity-30" />
          
          {/* Dot pattern */}
          <svg className="absolute inset-0 w-full h-full opacity-[0.02]">
            <defs>
              <pattern id="dots" width="24" height="24" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1" fill="black" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots)" />
          </svg>
        </motion.div>
      </div>

      <div className="relative z-10">
        {/* Hero Header - Refined */}
        <motion.header 
          initial="hidden"
          animate="visible"
          className="container mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-8 md:pb-12 max-w-7xl"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.6, -0.05, 0.01, 0.99] }}
            className="text-center"
          >
            <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-black tracking-tighter mb-6">
              {t('impact_page.our')}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-black via-gray-700 to-black">
                {t('impact_page.impact')}
              </span>
            </h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-gray-600 text-lg md:text-xl lg:text-2xl max-w-3xl mx-auto leading-relaxed"
            >
              {t('impact_page.lead', { defaultValue: t('impact_description') })}
            </motion.p>
          </motion.div>
        </motion.header>

        {/* Programs/Outcomes Section - NOW FIRST */}
        {outcomes.length > 0 && (
          <section className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6">
                  {t('impact_page.outcomes_title')}
                </h2>
                <p className="text-gray-600 text-lg md:text-xl max-w-2xl mx-auto">
                  {t('impact_page.outcomes_description')}
                </p>
              </motion.div>

              <motion.div 
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="grid grid-cols-1 lg:grid-cols-3 gap-8"
              >
                {(translatedOutcomes || outcomes).map((o, idx) => (
                  <motion.article 
                    key={idx}
                    variants={scaleIn}
                    className="group relative"
                  >
                    <motion.div 
                      className="h-full bg-white rounded-3xl p-8 md:p-10 border border-gray-100 shadow-sm hover:shadow-2xl transition-all duration-500"
                      whileHover={{ y: -8, transition: { duration: 0.3 } }}
                    >
                      {/* Program number */}
                      <div className="absolute -top-6 -right-6 w-16 h-16 bg-black text-white rounded-2xl flex items-center justify-center font-black text-2xl transform rotate-12 group-hover:rotate-0 transition-transform duration-300">
                        {idx + 1}
                      </div>
                      
                      {/* Icon with gradient background */}
                      <div className="w-20 h-20 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl mb-8 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <div className="w-10 h-10 bg-black rounded-xl" />
                      </div>
                      
                      <h3 className="font-black text-2xl md:text-3xl mb-4 tracking-tight">
                        <BackendText text={o.title} i18nKey={`impact_page.outcomes.${idx}.title`} fallback={o.title} />
                      </h3>
                      <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                        <BackendText text={o.desc} i18nKey={`impact_page.outcomes.${idx}.desc`} fallback={o.desc} />
                      </p>
                      
                      {Array.isArray(o.bullets) && o.bullets.length > 0 && (
                        <div className="space-y-4">
                          {o.bullets.map((b, i) => (
                            <motion.div 
                              key={i} 
                              className="flex items-start"
                              initial={{ opacity: 0, x: -20 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.1 }}
                              viewport={{ once: true }}
                            >
                              <div className="w-6 h-6 bg-yellow-50 rounded-full flex items-center justify-center mt-0.5 mr-3 flex-shrink-0">
                                <div className="w-2 h-2 bg-black rounded-full" />
                              </div>
                              <span className="text-gray-700">
                                <BackendText text={b} i18nKey={`impact_page.outcomes.${idx}.bullets.${i}`} fallback={b} />
                              </span>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  </motion.article>
                ))}
              </motion.div>
            </div>
          </section>
        )}

    

       <SuccessStories/>

        {/* Milestones - Improved Timeline */}
        {milestones.length > 0 && (
          <section className="py-16 md:py-24 bg-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6">
                  {t('impact_page.milestones_title')}
                </h2>
                <p className="text-gray-600 text-lg md:text-xl max-w-2xl mx-auto">
                  {t('timeline_description')}
                </p>
              </motion.div>
              
              <div className="relative max-w-5xl mx-auto">
                {/* Timeline line */}
                <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-black via-gray-400 to-black md:-translate-x-1/2" />
                
                <div className="space-y-12 md:space-y-16">
                  {(translatedMilestones || milestones).map((m, idx) => (
                    <motion.div 
                      key={idx}
                      initial={{ opacity: 0, x: idx % 2 === 0 ? -100 : 100 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] }}
                      className={`relative flex items-center ${
                        idx % 2 === 0 ? 'md:justify-start' : 'md:justify-end'
                      }`}
                    >
                      {/* Timeline dot */}
                      <motion.div 
                        className="absolute left-8 md:left-1/2 w-8 h-8 bg-black rounded-full md:-translate-x-1/2 z-10 flex items-center justify-center"
                        whileHover={{ scale: 1.3 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="w-3 h-3 bg-white rounded-full" />
                      </motion.div>
                      
                      {/* Content card */}
                      <motion.div 
                        className={`ml-20 md:ml-0 md:w-5/12 ${
                          idx % 2 === 0 ? 'md:pr-16' : 'md:pl-16'
                        }`}
                        whileHover={{ scale: 1.03 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                          <div className="inline-block px-4 py-1 bg-yellow-50 rounded-full mb-4">
                            <span className="font-black text-2xl">
                              <BackendText text={m.year} i18nKey={`impact_page.milestones.${idx}.year`} fallback={m.year} />
                            </span>
                          </div>
                          <p className="text-gray-700 text-lg leading-relaxed">
                            <BackendText text={m.text} i18nKey={`impact_page.milestones.${idx}.text`} fallback={m.text} />
                          </p>
                        </div>
                      </motion.div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Reports Section - Clean and Modern */}
        {reports.length > 0 && (
          <section className="py-16 md:py-24 bg-gray-50">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="text-center mb-12"
              >
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-6">
                  {t('impact_page.reports.title')}
                </h2>
              </motion.div>
             <Resources/>
            </div>
          </section>
        )}


      </div>
    </div>
  );
}

export default Impact;